import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, User2, Calendar, Search, RefreshCw, LogOut, Download, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import supabase from '../lib/supabaseClient';
import './AdminMessages.css';

const AdminMessages = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  const fetchMessages = async () => {
    setLoading(true);
    setError('');
      if (!supabase) {
        setError(t('admin_messages_missing_supabase'));
        setLoading(false);
        return;
      }

      try {
        const { data, error: fetchError } = await supabase
          .from('contact_messages')
          .select('id, name, email, phone, subject, message, created_at')
          .order('created_at', { ascending: false });

        if (fetchError) {
          throw fetchError;
        }

        setMessages(data || []);
      } catch (err) {
        setError(err.message || t('admin_messages_fetch_error'));
      } finally {
        setLoading(false);
      }
  };

  useEffect(() => {
    fetchMessages();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return messages;
    return messages.filter(m =>
      (m.name || '').toLowerCase().includes(q) ||
      (m.email || '').toLowerCase().includes(q) ||
      (m.phone || '').toLowerCase().includes(q) ||
      (m.subject || '').toLowerCase().includes(q) ||
      (m.message || '').toLowerCase().includes(q)
    );
  }, [messages, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleRefresh = () => {
    fetchMessages();
  };

  const handleExport = () => {
    const rows = filtered;
    const headers = ['name','email','phone','subject','message','created_at'];
    const csv = [headers.join(',')]
      .concat(
        rows.map(r => headers.map(h => `"${(r[h] ?? '').toString().replace(/"/g,'""')}"`).join(','))
      )
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contact_messages_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/admin/login');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message? This action cannot be undone.')) return;
    try {
      const { error: delError } = await supabase
        .from('contact_messages')
        .delete()
        .eq('id', id);
      if (delError) throw delError;
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to delete message');
    }
  };

  return (
    <div className="admin-messages">
      <header className="admin-header">
        <div className="admin-title">
          <h1>{t('admin_messages_title')}</h1>
          <p>{t('admin_messages_description')}</p>
        </div>
        <div className="admin-toolbar">
          <div className="search-box">
            <Search size={16} />
            <input
              aria-label="Search messages"
              placeholder="Search messages..."
              value={search}
              onChange={(e) => { setPage(1); setSearch(e.target.value); }}
            />
          </div>
          <button className="btn ghost" onClick={handleRefresh} title="Refresh">
            <RefreshCw size={16} />
            <span>Refresh</span>
          </button>
          <button className="btn ghost" onClick={handleExport} title="Export CSV">
            <Download size={16} />
            <span>Export</span>
          </button>
          <button className="btn danger" onClick={handleSignOut} title="Sign out">
            <LogOut size={16} />
            <span>Sign out</span>
          </button>
        </div>
      </header>

      {loading && (
        <div className="skeleton">
          <div className="skeleton-line" />
          <div className="skeleton-line" />
          <div className="skeleton-line" />
        </div>
      )}

      {error && !loading && (
        <div className="admin-state error">{error}</div>
      )}

      {!loading && !error && filtered.length === 0 && (
        <div className="admin-state empty">No messages match your search.</div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <>
          <div className="messages-stats">
            <span>Total: {filtered.length}</span>
            {filtered.length !== messages.length && (
              <span>Filtered from {messages.length}</span>
            )}
          </div>
          <div className="messages-table-wrapper">
            <table className="messages-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Subject</th>
                  <th>Message</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((msg) => (
                  <tr key={msg.id}>
                    <td className="cell-name" data-label="Name"><User2 size={14} /> {msg.name || t('admin_messages_unknown_name')}</td>
                    <td className="cell-email" data-label="Email"><Mail size={14} /> <a href={`mailto:${msg.email}`}>{msg.email}</a></td>
                    <td className="cell-phone" data-label="Phone">{msg.phone || '-'}</td>
                    <td className="cell-subject" data-label="Subject">{msg.subject}</td>
                    <td className="cell-message" data-label="Message">{msg.message}</td>
                    <td className="cell-date" data-label="Date">
                      <Calendar size={14} />{' '}
                      {msg.created_at ? new Date(msg.created_at).toLocaleString() : t('admin_messages_no_timestamp')}
                    </td>
                    <td className="cell-actions" data-label="Actions">
                      <button className="btn danger" onClick={() => handleDelete(msg.id)} title="Delete">
                        <Trash2 size={14} />
                        <span>Delete</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button className="btn ghost" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
              <span>Page {page} of {totalPages}</span>
              <button className="btn ghost" disabled={page === totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminMessages;
